import type { User } from '@datadog/browser-core';
import { v4 } from 'uuid';
import { ApolloError, GraphQLErrors, NetworkError } from '@apollo/client/errors';
import { Operation } from '@apollo/client';
import { ExecutionResult } from 'graphql';
import { ObjMap } from 'graphql/jsutils/ObjMap';
import Cookies from 'js-cookie';
import { RumInitConfiguration, datadogRum } from '@datadog/browser-rum';
import { deferUntil } from 'src/utils/deferUntil';
import { AuthErrorMetadata } from 'src/experiences/OAuth/Error/useOAuthErrorState';
import { CallbackSchedulerTimeSlotWithoutStartTime } from 'src/experiences/CallbackScheduler/types';
import { isSyntheticTest } from 'src/utils/validators/isSyntheticTest';
import { ZHLPersonalizedNextStepState } from '../graphql/generated/graphql';
import { ZillowSession } from '../auth/session/ZillowSessionManager';
import { SessionErrorMetadata } from '../auth/session/SessionErrorMetadata';
import { COOKIES_LOOKUP } from '../auth/constants';
import * as ApplicationConfigurationService from '../config/ApplicationConfigurationService.client';
import { FetchClientDomain } from '../fetch/FetchClient/domain';

declare global {
    // var must be used.
    // eslint-disable-next-line no-var
    var MAC_DD_RUM: RumInitConfiguration | undefined;
    // eslint-disable-next-line no-var
    var DD_RUM: typeof datadogRum | undefined;
}

export namespace DatadogRUMService {
    export enum CustomAction {
        BLEND_AUTH_START = 'homeloansdashboard.dashboard.auth.blend-auth-start',
        BLEND_AUTH_ERROR = 'homeloansdashboard.dashboard.auth.blend-auth-error',
        DASHBOARD_BOUNCE_PAGE_LOADED = 'dashboard.bounce-page',
        DASHBOARD_ACTIVATE_PAGE_SUCCESS = 'dashboard.activate-page-success',
        DASHBOARD_ACTIVATE_PAGE_FAILURE = 'dashboard.activate-page-failure',
        DASHBOARD_PAGE_LOADED = 'dashboard.dashboard-page',
        DASHBOARD_BOUNCE_TO_GOLDENEYE = 'dashboard.bounce-to-goldeneye',
        DASHBOARD_BOUNCE_TO_BLEND = 'dashboard.bounce-to-blend',
        DASHBOARD_BOUNCE_TO_DASHBOARD = 'dashboard.bounce-to-dashboard',
        DASHBOARD_START_BLEND_ERROR = 'dashboard.start-blend-error',
        DASHBOARD_START_BLEND_SUCCESS = 'dashboard.start-blend-success',
        DASHBOARD_START_FIRST_PARTY_EXPERIENCE_SUCCESS = 'dashboard.start-first-party-experience-success',
        DASHBOARD_START_FIRST_PARTY_EXPERIENCE_ERROR = 'dashboard.start-first-party-experience-error',
        DASHBOARD_BLEND_CTA_CLICK = 'dashboard.blend-cta-click',
        MIGRATION_REDIRECT = 'dashboard.migration-redirect',
        AUTHENTICATION_START = 'homeloansdashboard.dashboard.auth.session-start',
        AUTHENTICATION_SUCCESS = 'homeloansdashboard.dashboard.auth.authentication-success',
        CLICKSTREAM_EVENT = 'dashboard.clickstream-event',
        CALLBACK_SCHEDULER_START = 'homeloansdashboard.callback-scheduler.session-start',
        CALLBACK_SCHEDULER_TIME_SLOT_NOT_AVAILABLE = 'homeloansdashboard.callback-scheduler.time-slot-not-available',
        CALLBACK_SCHEDULER_SUCCESS_IMPRESSION = 'homeloansdashboard.callback-scheduler.success-impression',
        CALLBACK_SCHEDULER_FAILURE_IMPRESSION = 'homeloansdashboard.callback-scheduler.failure-impression',
        CALLBACK_SCHEDULER_CANCEL_CTA_CLICK = 'homeloansdashboard.callback-scheduler.cancel-cta-click',
        CALLBACK_SCHEDULER_BOOK_APPOINTMENT_CLICK = 'homeloansdashboard.callback-scheduler.book-appointment-click',
        CALLBACK_SCHEDULER_FATAL_ERROR = 'homeloansdashboard.callback-scheduler.fatal-error',
        CALLBACK_SCHEDULER_GRAPH_ERROR = 'homeloansdashboard.callback-scheduler.non-fatal-error',
        CALLBACK_SCHEDULER_NEXT_STEP_CTA_CLICK = 'homeloansdashboard.callback-scheduler.next-step-cta-click',
        CALLBACK_SCHEDULER_EMPTY_SLOTS_ERROR = 'homeloansdashboard.callback-scheduler.empty-slots-error',
        NEW_ACTIVATION_EMAIL_MISMATCH = 'homeloansdashboard.activation.new-activation-email-mismatch',
        USER_USED_DIFFERENT_EMAIL_THAN_HINT = 'homeloansdashboard.auth.user-used-different-email-than-hint',
        USER_USED_SAME_EMAIL_AS_HINT = 'homeloansdashboard.auth.user-used-same-email-as-hint',
        UNIVERSAL_FLOW_CALL_SCHEDULER_FEATURE_NOT_IMPLEMENTED_IMPRESSION = 'homeloansdashboard.universal-flow-call-scheduler.feature-not-implemented-impression',
        FEATURE_RETIRED_PAGE_IMPRESSION = 'homeloansdashboard.feature-retired.page-impression',
        DASHBOARD_GRAPHQL_HISTOGRAM = 'dashboard.graphql-histogram',
        SIGNIN_BOUNCE_REDIRECT_DETERMINATION_STARTED = 'homeloansdashboard.signin-bounce.redirect-determination-started',
        SIGNIN_BOUNCE_NESTED_CALLBACK_DETECTED = 'homeloansdashboard.signin-bounce.nested-callback-detected',
        SIGNIN_BOUNCE_USING_ORIGINAL_CALLBACK = 'homeloansdashboard.signin-bounce.using-original-callback',
        SIGNIN_BOUNCE_REDIRECTING_TO_CALLBACK = 'homeloansdashboard.signin-bounce.redirecting-to-callback',
        SIGNIN_BOUNCE_USING_FALLBACK_REDIRECT = 'homeloansdashboard.signin-bounce.using-fallback-redirect',
    }
    type CustomDatadogActionConfig = {
        /**
         * If set to true, the action will be deferred until the user is identified in datadog
         */
        deferUntilUserIsIdentified?: boolean;
        /**
         * If set to true, no matter how often you call the logging function, it will only execute the
         * first time for a page load
         */
        onlyLogOnce?: boolean;
    };

    /**
     * This is the list of pre-created facets in datadog. You should leverage them where possible instead
     * of creating new context values to allow for immediate aggregation of the data in datadog dashboards.
     *
     * If you need to introduce new facets, please create them in datadog and add them here
     */
    type DatadogFacets = {
        /**
         * The location that the user requested before we redirected them to login
         */
        loginLocation: string | null;

        /**
         * The next destination for a user
         */
        nextDestination: string | null;

        /**
         * The selected timeslot for callback scheduler
         */
        selectedTimeSlot: CallbackSchedulerTimeSlotWithoutStartTime | null;

        /**
         * The loan ID of the loan loaded on a dashboard (may not necessarily be the currently selected loan
         * since the dashboard can be manually loaded with a different loan ID)
         */
        loanID: string | null;

        /**
         * @deprecated - use authError instead
         * The cause of a session error
         */
        cause: SessionErrorMetadata | null;

        /**
         * @deprecated - No longer used except for domain migration
         * Where a user was coming from
         */
        from: string | null;

        /**
         * @deprecated - No longer used except for domain migration
         * Where a user was redirected to
         */
        to: string | null;

        /**
         * @deprecated - use errorMessage or apolloError or authError or graphQLErrors instead
         * An arbitrary error
         */
        error: string | ApolloError | GraphQLErrors | null | AuthErrorMetadata;

        /**
         * @deprecated - use errorMessage or graphQLErrorsAsString instead
         * An arbitrary error
         */
        errorAsString: string | null;

        /**
         *
         */
        authError: AuthErrorMetadata | SessionErrorMetadata | null;

        /**
         * An apollo client error
         */
        apolloError: ApolloError | null;

        /**
         * The lead ID for the loan loaded on a dashboard
         */
        leadID: string | null;

        /**
         * The borrower ID for the user
         */
        borrower_id: string | null;

        /**
         * The current personalized next step stage for the user
         * @deprecated use personalizedNextStepStage
         */
        status: ZHLPersonalizedNextStepState | null;

        /**
         * @deprecated - use nextDestination instead
         * The landing url for a user who has just activated
         */
        landingUrl: string | null;

        /**
         * The current personalized next step stage for the user
         */
        personalizedNextStepStage: ZHLPersonalizedNextStepState | null;

        /**
         * The user's zillow session
         */
        session: ZillowSession | null;

        /**
         * The activation token for a failed activation
         */
        activationToken: string | null;

        /**
         * Any graphQL errors that may have occurred
         */
        graphQLErrorsAsString: string | null;

        /**
         * The GraphQL response that came in to the Apollo Client
         */
        response: ExecutionResult<ObjMap<unknown>, ObjMap<unknown>> | null;

        /**
         * The operation that was executed in Apollo Client
         */
        operation: Operation | null;

        /**
         * Any network error that occurred in Apollo Client
         */
        networkError: NetworkError | null;

        /**
         * Any graphQL errors that may have occurred
         */
        graphQLErrors: GraphQLErrors | null;

        /**
         * The error code for a graphQL error. Also used by Blend's error reporting
         */
        errorCode: string | null;

        /**
         * The error message for a given error. Also used by Blend's error reporting
         */
        errorMessage: string | null;

        /**
         * What our system considers the user's currently selected loan to be
         */
        currentlySelectedLoan: string | null;

        /**
         * The number of loans available to the user
         */
        numberOfLoans: number | null;

        /**
         * The lead source for the currently selected loan
         */
        leadSource: string | null;

        /**
         * The lead type for the currently selected loan
         */
        leadType: string | null;

        /**
         * The lead program for the currently selected loan
         */
        leadProgram: string | null;

        /**
         * The returned graphql data for a query or mutation
         */
        graphqlData: Record<string, unknown> | null;

        /**
         * Arbitrary data that can be passed to datadog
         */
        data: Record<string, unknown> | null;

        /**
         * @deprecated - use restRequest
         * The url a request was made to
         */
        url: RequestInfo | URL | null;

        /**
         * @deprecated - use restRequest
         * The request options
         */
        opts: FetchClientDomain.TimeoutableFetchConfig | null;

        /**
         * @deprecated - use restRequest
         * The request timeout
         */
        timeout: number | null;

        /**
         * The configuration for a rest request
         */
        restRequest: {
            url: RequestInfo | URL | null;
            opts: FetchClientDomain.TimeoutableFetchConfig | null;
            timeout: number | null;
        };

        /**
         * Experience Graph Context
         */
        experienceGraphActionContext: {
            actionSource: string;
            actionsLookup: object;
            analyticsLookup: object;
            metadata: object;
        };

        /**
         * The email that we preferred the user to use for login when she was prompted to sign in
         */
        preferredLoginEmail: string | null;

        /**
         * Whether a login hint was displayed during authentication
         */
        loginHintDisplayed: boolean | null;
    };

    /**
     * Encapsulates all the behavior that we support with datadog, but wrapped with defer logic to defer loading datadog
     * until necessary to save on bundle size, and also to defer optionally defer logging telemetry until the user is identified
     */
    export class Client {
        private static instance: Client;
        private readonly DATADOG_USER_SET_FLAG = 'homeloansdashboard.datadog.user-identified';

        private readonly DEFER_POLL_RATE_IN_MS = 100;

        private client: typeof datadogRum;

        private currentIdempotencyToken: string;

        private idempotentTelemetryRecord: Record<string, string>;

        private _isUserIdentified = false;

        private constructor() {
            this.idempotentTelemetryRecord = {};
            this.currentIdempotencyToken = v4();
            const { addAction, addError, ...rest } = datadogRum;
            const wrapContext = <T extends object | undefined>(context: T) => ({
                zhlMortgageApplicationCenterWeb: {
                    zguid: Cookies.get(COOKIES_LOOKUP.SESSION_GUID),
                    ...context,
                },
            });
            const wrappedAddAction = (...args: Parameters<typeof addAction>): ReturnType<typeof addAction> => {
                // intentionally not logged actions for synthetic tests because of a conversation we had where
                // synthetic tests were polluting our data and masking real users
                if (isSyntheticTest()) {
                    return;
                }
                const [action, unwrappedContext] = args;
                addAction(action, wrapContext(unwrappedContext));
            };
            const wrappedAddError = (...args: Parameters<typeof addError>): ReturnType<typeof addError> => {
                const [error, unwrappedContext] = args;
                addError(error, wrapContext(unwrappedContext));
            };

            this.client = {
                ...rest,
                addAction: wrappedAddAction,
                addError: wrappedAddError,
            } as typeof datadogRum;
        }

        /**
         * Initializes the global datadog instance. This should only be done once, and shouldn't be directly consumed
         * by code for experiences.
         */
        public static initializeDatadogSDK() {
            if (!globalThis.MAC_DD_RUM) {
                const config =
                    ApplicationConfigurationService.ConfigProvider.getInstance().datadogConfig.rumSDKConfiguration;
                datadogRum.init(config);
                globalThis.MAC_DD_RUM = config;
            }
        }

        public static getInstance() {
            if (!this.instance) {
                this.instance = new Client();
            }
            return this.instance;
        }

        public get isUserIdentified() {
            return this._isUserIdentified;
        }
        public clearIdempotencyHistory() {
            this.currentIdempotencyToken = v4();
            this.idempotentTelemetryRecord = {};
        }

        async addAction<TContext extends DatadogFacets | undefined>(
            action: string,
            context?: Partial<TContext>,
            config: CustomDatadogActionConfig = {}
        ) {
            await this.performTelemetry(() => this.client.addAction(action, context), config, action);
        }
        async setUser(user: User & { id: string }) {
            this.client.setUser(user);
            this._isUserIdentified = true;
        }

        async clearUser() {
            this._isUserIdentified = false;
            this.client.clearUser();
        }

        async addFeatureFlagEvaluation<TExperimentName extends string, TValue>(
            experimentName: TExperimentName,
            experimentValue: TValue
        ) {
            this.client.addFeatureFlagEvaluation(experimentName, experimentValue);
        }

        async addError<TError extends Error, TContext extends DatadogFacets | undefined>(
            error: TError,
            context?: Partial<TContext>,
            config: CustomDatadogActionConfig = {}
        ) {
            await this.performTelemetry(() => this.client.addError(error, context), config, error.name);
        }

        private async performTelemetry(
            logTelemetry: () => void,
            config: CustomDatadogActionConfig,
            actionOrError: string
        ) {
            if (config.onlyLogOnce && this.idempotentTelemetryRecord[actionOrError] === this.currentIdempotencyToken) {
                return;
            }
            if (!config.deferUntilUserIsIdentified || this.isCurrentUserIdentified()) {
                this.registerIdempotentTelemetry(actionOrError);
                return logTelemetry();
            }
            await deferUntil(
                this.isCurrentUserIdentified.bind(this),
                this.performAndRegisterIdempotentTelemetry.bind(this, actionOrError, logTelemetry),
                this.DEFER_POLL_RATE_IN_MS
            );
        }

        private performAndRegisterIdempotentTelemetry(action: string, logTelemetry: () => void) {
            this.registerIdempotentTelemetry(action);
            logTelemetry();
        }

        private registerIdempotentTelemetry(action: string) {
            this.idempotentTelemetryRecord[action] = this.currentIdempotencyToken!;
        }

        private isCurrentUserIdentified() {
            return this._isUserIdentified;
        }
    }
}
